import React, { useLayoutEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import SectionHeading from './SectionHeading';
import StepGlyph from './StepGlyph';
import Icon from './Icon';
import { howWeWork } from '../content/company';

/**
 * Як ми працюємо — п'ять кроків, з'єднаних дорогою.
 *
 * Відповідь на питання, яке ставить будь-який відвідувач: «що буде після
 * того, як я напишу?». П'ять кроків із company.js — той самий перелік, що й
 * на /pro-kompaniyu. Стоїть перед формою навмисно: людина читає, що
 * станеться після звернення, і одразу під цим може звернутися.
 *
 * Перша редакція була рядом із номерами на прямій лінії — власник попросив
 * «щось цікаве візуально». Тепер це дорога: крива лінія, що проходить через
 * усі п'ять кроків, і в кожного кроку власний малюнок (StepGlyph). На
 * широкому екрані кроки стоять у ряд, кожен другий трохи нижче, тож дорога
 * хвилею йде зліва направо; на планшеті — зигзагом між двома-трьома
 * стовпцями; на телефоні — вертикально вздовж лівого краю.
 *
 * Дорога не намальована наперед: після розкладки вимірюються центри
 * малюнків і між ними будуються плавні криві (useLayoutEffect +
 * ResizeObserver). Тому вона правильна на будь-якій ширині й при будь-якому
 * шрифті — координати беруться з реальної розмітки, а не вгадуються.
 * Малюється лінія тоді, коли секція з'являється на екрані (pathLength).
 */
const buildPath = (points) => {
  if (points.length < 2) return '';
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1];
    const b = points[i];
    // Горизонтальний перехід (кроки в ряд) — S-подібна крива з
    // горизонтальними дотичними; вертикальний (стовпець) — з вертикальними.
    if (Math.abs(b.x - a.x) > Math.abs(b.y - a.y)) {
      const cx = (a.x + b.x) / 2;
      d += ` C ${cx} ${a.y}, ${cx} ${b.y}, ${b.x} ${b.y}`;
    } else {
      const cy = (a.y + b.y) / 2;
      d += ` C ${a.x} ${cy}, ${b.x} ${cy}, ${b.x} ${b.y}`;
    }
  }
  return d;
};

const HowWeWork = () => {
  const [ref, inView] = useInView({ threshold: 0.15, triggerOnce: true });
  const listRef = useRef(null);
  const [road, setRoad] = useState({ d: '', w: 0, h: 0, show: false });

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return undefined;

    const compute = () => {
      const rect = list.getBoundingClientRect();
      const points = Array.from(list.querySelectorAll('[data-node]')).map((node) => {
        const r = node.getBoundingClientRect();
        return { x: r.left - rect.left + r.width / 2, y: r.top - rect.top + r.height / 2 };
      });
      // Дорогу малюємо тільки в двох розкладках: один стовпець (вона йде
      // вертикально вздовж малюнків) і п'ять кроків у ряд (йде хвилею).
      // У проміжних — два-три стовпці — крива між сусідами проходила б
      // просто крізь текст сусіднього кроку, і це читалося як дефект.
      const single = points.every((pt, i) => i === 0 || Math.abs(pt.x - points[0].x) < 1);
      // «Усі в одному ряду» рахуємо не по y (кожен другий крок навмисно
      // зсунутий униз), а по x: якщо всі п'ять стоять на різних вертикалях,
      // ряд один.
      const xs = new Set(points.map((pt) => Math.round(pt.x)));
      const oneRow = xs.size === points.length;
      setRoad({ d: buildPath(points), w: rect.width, h: rect.height, show: single || oneRow });
    };

    compute();
    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(compute);
      observer.observe(list);
    }
    window.addEventListener('resize', compute);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', compute);
    };
  }, []);

  return (
    <section id="how-we-work" className="py-16 lg:py-24 bg-slate-50" ref={ref}>
      <div className="section-container">
        <motion.div
          className="mb-10 lg:mb-14"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <SectionHeading
            caption="Що буде після звернення"
            title="Як ми працюємо"
            description="Людина, яка обирає юриста, питає не «які у вас цінності», а «що буде після того, як я напишу». Ось відповідь — у п’ять кроків."
            badge={{ value: '05', label: 'Кроків' }}
          />
        </motion.div>

        <ol
          ref={listRef}
          className="relative list-none grid gap-x-6 gap-y-8 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
        >
          {/* data-client-only: дорога існує лише після вимірювань, тобто в
              першому клієнтському рендері її ще немає. Якби вона лишилась у
              пререндереному HTML, гідрація не збіглася б і React перемалював
              би всю сторінку, тож пререндер її вирізає. Та й координати
              знімка з вікна 1366px на іншій ширині були б неправильні. */}
          {road.show && road.d && road.w > 0 && (
            <svg
              data-client-only
              className="absolute inset-0 w-full h-full pointer-events-none"
              viewBox={`0 0 ${road.w} ${road.h}`}
              preserveAspectRatio="none"
              aria-hidden="true"
              focusable="false"
            >
              <path d={road.d} fill="none" stroke="#99f6e4" strokeWidth="2" />
              <motion.path
                d={road.d}
                fill="none"
                stroke="#14b8a6"
                strokeWidth="2"
                strokeLinecap="round"
                initial={{ pathLength: 0 }}
                animate={inView ? { pathLength: 1 } : {}}
                transition={{ duration: 1.8, ease: 'easeInOut', delay: 0.3 }}
              />
            </svg>
          )}

          {howWeWork.map((step, index) => (
            <motion.li
              key={step.label}
              className={`relative flex gap-4 xl:flex-col xl:items-center xl:text-center ${
                index % 2 === 1 ? 'xl:mt-16' : ''
              }`}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 + index * 0.12 }}
            >
              {/* Вузол дороги: непрозоре тло, щоб лінія не перекреслювала малюнок. */}
              <span
                data-node
                className="relative z-10 flex-shrink-0 w-16 h-16 rounded-2xl bg-white border border-teal-200 shadow-sm text-teal-700 flex items-center justify-center"
              >
                <StepGlyph index={index} className="w-8 h-8" />
                <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-teal-700 text-white text-[0.65rem] font-bold flex items-center justify-center">
                  {index + 1}
                </span>
              </span>

              <div className="relative z-10 xl:mt-4">
                <h3 className="font-display font-bold text-slate-900 leading-snug">
                  <span className="sr-only">Крок {index + 1}. </span>
                  {step.label}
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{step.text}</p>
              </div>
            </motion.li>
          ))}
        </ol>

        <Link
          to="/pro-kompaniyu"
          className="mt-12 inline-flex items-center gap-2 text-teal-700 font-semibold hover:text-teal-800 transition-colors duration-200"
        >
          Докладніше про порядок роботи
          <Icon name="fa-arrow-right" />
        </Link>
      </div>
    </section>
  );
};

export default HowWeWork;
